/* 
 * This file is created by Vatsaly Patel
 * Note: If you are editing anything, please let me know - or do comment [it's compulsory]
*/

import React, { useRef, useCallback, useState, useEffect } from "react";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import { CircularProgress } from "@material-ui/core";
import { getPaymentSummariesList } from "../../apiRoutes";
import moment from "moment-timezone";
import Label from "../../components/label";

/* 
 * createData used for filling data into Material UI table
*/

function createData(
  _id,
  callMadeAt,
  updatedAt,
  paymentMadeAt,
  receiptUrl,
  paymentSlip,
  appointmentId,
  createdAt,
  appointmentPrice,
  PMFeeAmount,
  taxAmount,
  taxSummary,
  totalTaxAmt
) {
  return {
    _id,
    callMadeAt,
    updatedAt,
    paymentMadeAt,
    receiptUrl,
    paymentSlip,
    appointmentId,
    createdAt,
    appointmentPrice,
    PMFeeAmount,
    taxAmount,
    taxSummary,
    totalTaxAmt
  };
}
export default function StickyHeadTable(props) {
  const observer = useRef(); // to check it is the last element or not - if it is last element then call an API
  const [pageNo, setPageNumber] = useState(1); // just set page number if needed in future. 
  const [nextPage, setNextPage] = useState(false); // set next page if there is more data 
  const [loading, setLoading] = useState(true); // loading variable used to reduce multiple API calls
  const [progressBar, setProgressBar] = useState(false); // progress bar hide and show
  const [limit, setLimit] = useState(10); // limit is fixed but if we need to change in future then change it here
  const [skip, setSkip] = useState(0); // set Skip page
  const [noMoreData, setNoMoreData] = useState(false); // no data found block hide-show



  // Data to be appended
  const [rows, setRows] = useState([]);

  // download tax summary
  const downloadTaxSummary = (event, href) => {
    window.open(href, "_blank");
  };

  /*
   * Check last element is visible or not on scroll
  */

  const lastElementRef = useCallback(node => {
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(data => {
      if (data) {
        if (data[0].isIntersecting && nextPage && loading) {
          setLoading(false);
          if (loading) {
            let skipCalculate = limit * pageNo,
              currentPageNo = pageNo + 1;
            setPageNumber(currentPageNo);
            setSkip(skipCalculate);
            getPaymentSummary(limit, skipCalculate, currentPageNo);
          }
          setTimeout(() => {
            setLoading(true);
          }, 2000);
        }
      }
    });
    if (node) observer.current.observe(node);
  });

  /*
  * Main function to call an API
  * Pass parameters compulusry (limit, skipPage, currentPage) to calculate pagination
  */

  const getPaymentSummary = useCallback((limitPage, skipPage, currentPage) => {
    let updatedParam = {
      limit: limitPage,
      skip: skipPage,
      startDate: new Date(moment(props.startDate).format("MM/DD/YYYY")).toISOString(),
      endDate: new Date(new Date(props.endDate).getFullYear(), new Date(props.endDate).getMonth(), new Date(props.endDate).getDate() + 1, 0,0,0).toISOString()
      // endDate: new Date(moment(props.endDate).format("MM/DD/YYYY")).toISOString(),
    };
    let apiCallParam = {
      reqType: "GET",
      reqURL: getPaymentSummariesList,
      dataObj: {},
      params: updatedParam,
      headers: {},
      successCallback: res => {
        // calculate is there any next page or not ?
        let currentRowCount = res.data.data.count - (currentPage * limitPage);
        if (currentRowCount > 0) {
          setNextPage(true);
        } else {
          setNextPage(false);
          setNoMoreData(true);
          setProgressBar(false);
        }

        // set the data
        let paySummaryData = [];
        res.data.data.paymentHistory.map((value, index) => {
          paySummaryData.push(
            createData(
              value._id,
              value.callMadeAt ? moment(value.callMadeAt).tz("Australia/Brisbane").format("DD-MM-YYYY") : "",
              value.updatedAt ? moment(value.updatedAt).tz("Australia/Brisbane").format("DD-MM-YYYY") : "",
              value.paymentMadeAt ? moment(value.paymentMadeAt).tz("Australia/Brisbane").format("DD-MM-YYYY") : "",
              value.receiptUrl,
              value.paymentSlip,
              value.appointmentId,
              value.createdAt ? moment(value.createdAt).tz("Australia/Brisbane").format("DD-MM-YYYY") : "",
              '$' + value.appointmentPrice.toFixed(2),
              '$' + value.PMFeeAmount.toFixed(2),
              '$' + value.taxAmount.toFixed(2),
              value.taxSummary,
              '$' + ((value.taxAmount) + (value.PMFeeAmount)).toFixed(2)
            )
          );
        });
        if (skipPage === 0) {
          setRows(paySummaryData);
        } else {
          setRows(prev => prev.concat(paySummaryData));
        }
      },
      failureCallback: err => {
        console.log("Something went wrong. Please try again ", err);
        setNextPage(false);
        setNoMoreData(true);
        setProgressBar(false);
      },
      showPopUp: false
    };
    props.makeAPICall(apiCallParam);
  });

  /*
  * Reset pagination
  */
  const resetPagination = () => {
    setPageNumber(1);
    setNextPage(false);
    setLoading(true);
    setProgressBar(true);
    setSkip(0);
    setNoMoreData(false);
  };

  /*
   * Watch on filter
  */
  useEffect(() => {
    resetPagination();
    setRows([]);
    getPaymentSummary(limit, 0, 1);
  }, [props.dateFilterApply]);


  return (
    <Paper className="w-100 tax-invoice-list-div">
      <TableContainer className="table-max-height primary-table-color">
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              <TableCell>Tax Invoices</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>PM Fee</TableCell>
              <TableCell>GST on PM Fee</TableCell>
              <TableCell>Total Invoice</TableCell>
              <TableCell>Download</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Loop over the data (createData function) */}
            {rows.map((row, index) => {

              /*
               * If it is last element then take the referent to call an API Else print another block
              */

              if (rows.length === index + 1) {
                return (
                  <TableRow ref={lastElementRef} hover key={row._id}>
                    <TableCell
                      className={(noMoreData && !rows.length) || (noMoreData && rows.length === limit) || (noMoreData && pageNo > 1) ? "primary-color" : "primary-color border-bottom-0"}>
                      {row.appointmentId}{" "}
                    </TableCell>
                    <TableCell className={(noMoreData && !rows.length) || (noMoreData && rows.length === limit) || (noMoreData && pageNo > 1) ? "" : "border-bottom-0"}>{row.createdAt} </TableCell>
                    <TableCell className={(noMoreData && !rows.length) || (noMoreData && rows.length === limit) || (noMoreData && pageNo > 1) ? "" : "border-bottom-0"}>{row.PMFeeAmount} </TableCell>
                    <TableCell className={(noMoreData && !rows.length) || (noMoreData && rows.length === limit) || (noMoreData && pageNo > 1) ? "" : "border-bottom-0"}>{row.taxAmount} </TableCell>
                    <TableCell className={(noMoreData && !rows.length) || (noMoreData && rows.length === limit) || (noMoreData && pageNo > 1) ? "" : "border-bottom-0"}>{row.totalTaxAmt} </TableCell>
                    <TableCell
                      className={(noMoreData && !rows.length) || (noMoreData && rows.length === limit) || (noMoreData && pageNo > 1) ? "cursor-pointer" : "cursor-pointer border-bottom-0"}
                      onClick={event =>
                        downloadTaxSummary(event, row.taxSummary)
                      }
                    >
                      <i className="fas fa-download primary-color"></i>{" "}
                      <span className="ml-3">Download</span>
                    </TableCell>
                  </TableRow>
                );
              } else {
                return (
                  <TableRow hover key={row._id}>
                    <TableCell className="primary-color">
                      {row.appointmentId}{" "}
                    </TableCell>
                    <TableCell>{row.createdAt} </TableCell>
                    <TableCell>{row.PMFeeAmount} </TableCell>
                    <TableCell>{row.taxAmount} </TableCell>
                    <TableCell>{row.totalTaxAmt} </TableCell>
                    <TableCell
                      className="cursor-pointer"
                      onClick={event =>
                        downloadTaxSummary(event, row.taxSummary)
                      }
                    >
                      <i className="fas fa-download primary-color"></i>{" "}
                      <span className="ml-3">Download</span>
                    </TableCell>
                  </TableRow>
                );
              }
            })}

            {/* 
              * Progress bar on loading of APIs 
            */}

            <TableRow className={progressBar ? "" : "d-none"}>
              <TableCell
                colSpan={7}
                className="text-center progress-bar-center"
              >
                <CircularProgress disableShrink className="primary-loader" />
              </TableCell>
            </TableRow>

            {/* 
              * No data fount if there are no data array in API
            */}

            <TableRow className={(noMoreData && !rows.length) || (noMoreData && rows.length === limit) || (noMoreData && pageNo > 1) ? "" : "d-none"}>
              <TableCell
                colSpan={7}
                className="text-center progress-bar-center border-bottom-0"
              >
                <Label title="No data found" />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
